import { useEffect, useState, useCallback, useRef } from "react";
import { useInventoryFreshness } from "database/useDatabaseReader";
import { getFirestore, onSnapshot, doc } from "firebase/firestore";
import { getProductsByDisplayIds, groupByItemNumber } from "services/supabaseSearch";
import { processInventoryData } from "helpers/typesenseInventoryTransform";
import { colorCodeRanking } from "helpers/stormtechColorCodes";

export const useProjectBuilderInventory = (projID, warehouses) => {
  const [initialProjectData, setInitialProjectData] = useState();
  const [articles, setArticles] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const previousWarehousesRef = useRef(null);

  //Function to get article data & inventory
  const getArticlesData = useCallback(
    async (articleIDs, warehouses, replaceMode = false) => {
      // Only clear inventory in replace mode to preserve existing data when appending
      if (replaceMode) {
        setInventory([]);
      }
      setInventoryLoading(true);

      const allIds = articleIDs.map((item) => item.id || item);
      const products = await getProductsByDisplayIds(allIds);
      const allGroupedHits = groupByItemNumber(products);

      const finalResult = [];

      allGroupedHits.forEach((group) => {
        const groupHits = group.colors;

        //Sort the colors
        const singleColor = [];
        const multipleColors = [];
        const clearanceColors = [];

        groupHits.forEach((hit) => {
          if (
            (hit.secondaryColorCode == null ||
              hit.primaryColorCode === hit.secondaryColorCode) &&
            !hit.isClearance
          ) {
            singleColor.push(hit);
          } else if (!hit.isClearance) {
            multipleColors.push(hit);
          } else {
            clearanceColors.push(hit);
          }
        });

        singleColor.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
            colorCodeRanking.indexOf(b.primaryColorCode),
        );

        multipleColors.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
              colorCodeRanking.indexOf(b.primaryColorCode) ||
            colorCodeRanking.indexOf(a.secondaryColorCode[0]) -
              colorCodeRanking.indexOf(b.secondaryColorCode[0]),
        );

        clearanceColors.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
            colorCodeRanking.indexOf(b.primaryColorCode),
        );

        const sortedGroupHits = [
          ...singleColor,
          ...multipleColors,
          ...clearanceColors,
        ];
        // console.log(sortedGroupHits);

        finalResult.push({
          itemNumber: group.itemNumber,
          colors: sortedGroupHits,
        });
      });

      if (replaceMode) {
        setArticles(finalResult);
      } else {
        setArticles((prev) => [...prev, ...finalResult]);
      }

      // Process inventory data synchronously, then commit in ONE state update.
      // (Previously called setInventory once per colour, which is O(n^2) object
      // spreads and a render storm on large projects. Same final object, built
      // once.)
      const mergedInventory = {};
      finalResult.forEach(({ colors }) => {
        colors.forEach((colorData) => {
          Object.assign(
            mergedInventory,
            processInventoryData(colorData, warehouses).data,
          );
        });
      });
      setInventory((prevData) => ({ ...prevData, ...mergedInventory }));

      // console.log("Done getting inventory");
      // console.log(currentInventory);

      setInventoryLoading(false);
    },
    [],
  );

  // Memoize the refresh callback to prevent infinite re-renders
  const handleInventoryRefresh = useCallback(() => {
    // Only reload if we have project data and warehouses
    if (initialProjectData && initialProjectData?.warehouses?.length > 0) {
      console.log(`Auto-reloading project builder inventory - fresh inventory data available`);
      const articleIDs = initialProjectData.selectedItems?.map((item) => item.id) || [];
      if (articleIDs.length > 0) {
        getArticlesData(articleIDs, initialProjectData.warehouses, true); // Replace mode for auto-refresh
      }
    }
  }, [initialProjectData, getArticlesData]);

  // Auto-reload when fresh inventory data is available (completion-based)
  const { isStale, minutesSinceUpdate } = useInventoryFreshness(handleInventoryRefresh); // Uses completion-based refresh by default

  //Get initial project data with real-time updates
  useEffect(() => {
    if (projID == null || warehouses == null) return;
    const db = getFirestore();
    
    const unsubscribe = onSnapshot(doc(db, "projects", projID), (doc) => {
      if (doc.exists()) {
        setInitialProjectData({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt.toDate(),
          modifiedAt: doc.data().modifiedAt.toDate(),
        });
      }
    });

    return () => unsubscribe();
  }, [projID, warehouses]);

  // Load article + inventory data ONCE per project (initial load only).
  //
  // Subsequent selectedItems changes are handled incrementally:
  //   - adds    -> addArticleForInventory appends just the new item
  //   - removes -> the render-time selectedItems filter in ProjectBuilderPage
  //                hides them (any stale cache entry is harmless)
  //
  // Previously this effect re-fetched and rebuilt ALL items on every Firestore
  // snapshot (every keystroke add, plus the articleSearchKey write-back), which
  // is what froze large projects. Warehouse changes and inventory-freshness
  // refreshes still trigger their own full reloads below.
  const hasLoadedRef = useRef(false);

  // Reset the once-guard when switching projects so the next project loads.
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [projID]);

  useEffect(() => {
    if (initialProjectData == null || hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    const projectWarehouses = initialProjectData.warehouses;
    const articleIDs = initialProjectData.selectedItems?.map((item) => item.id);
    getArticlesData(articleIDs, projectWarehouses, true); // Replace mode for initial load
  }, [initialProjectData, getArticlesData]);

  // Reload articles when warehouses change to get fresh data for new warehouses
  useEffect(() => {
    if (initialProjectData == null) return;
    
    const currentWarehouses = initialProjectData.warehouses;
    const articleIDs = initialProjectData.selectedItems?.map((item) => item.id);
    
    // Check if warehouses actually changed (not initial load)
    const currentWarehouseIds = currentWarehouses?.map(w => w.letterCode).sort().join(',');
    const previousWarehouseIds = previousWarehousesRef.current;
    
    if (previousWarehouseIds && currentWarehouseIds !== previousWarehouseIds && articleIDs && articleIDs.length > 0) {
      getArticlesData(articleIDs, currentWarehouses, true); // Complete reload with new warehouse list
    }
    
    // Update the ref for next comparison
    previousWarehousesRef.current = currentWarehouseIds;
  }, [initialProjectData?.warehouses, getArticlesData]);

  const addArticleForInventory = useCallback(
    (articleID) => {
      const [itemNumber, color] = articleID.split("__");
      const existingArticle = articles.find(
        (article) =>
          article.itemNumber === itemNumber &&
          article.colors.find((colorData) => colorData.color === color),
      );
      if (existingArticle == null) {
        // Use the PROJECT's warehouses (same as the initial load) so an appended
        // item's inventory matches the columns the card renders. Previously this
        // used the global warehouse list and relied on the per-snapshot full
        // reload to overwrite it; that reload is now gone.
        getArticlesData([articleID], initialProjectData?.warehouses, false); // Append mode
      }
    },
    [getArticlesData, initialProjectData, articles],
  );

  return { 
    addArticleForInventory, 
    inventory, 
    inventoryLoading,
    isInventoryStale: isStale,
    minutesSinceInventoryUpdate: minutesSinceUpdate,
    articles,
  };
};
